'use client'

import { Checkbox, Field, Label } from '@headlessui/react'
import clsx from 'clsx'
import Icon from 'lib/components/Icon/Icon'
import { ControlledFormField, FormFieldCommonProps } from 'lib/types/Form'
import { forwardRef, useCallback, useEffect, useState } from 'react'
import { Controller } from 'react-hook-form'
import InputError from '../InputError/InputError'

interface CheckboxInputStandaloneProps
  extends Omit<FormFieldCommonProps, 'defaultValue'> {
  /**
   * Whether the checkbox is checked by default
   */
  checked?: boolean
  /**
   * Optional class name to append to component's classname
   */
  className?: string
  /**
   * Change event handler
   */
  onChange?: (data: boolean) => void
  /**
   * Optional class name for the label
   */
  labelClassName?: string
}

export interface CheckboxInputProps
  extends Omit<ControlledFormField, 'defaultValue'>,
    CheckboxInputStandaloneProps {}

// Standalone component that can be used outside react-hook-form's context
export const CheckboxInputStandalone = forwardRef<
  HTMLInputElement,
  CheckboxInputStandaloneProps
>(function CheckboxInputStandaloneWithRef(
  {
    name,
    checked = false,
    label,
    disabled = false,
    className,
    error,
    onChange,
    labelClassName,
  }: CheckboxInputStandaloneProps,
  ref,
) {
  const [checkedCached, setCheckedCached] = useState(checked)
  const [enabled, setEnabled] = useState(checkedCached)

  const onEnableChange = useCallback(
    (isEnabled: boolean) => {
      setEnabled(isEnabled)
      onChange?.(isEnabled)
    },
    [onChange],
  )

  useEffect(() => {
    if (checked !== checkedCached) {
      setCheckedCached(checked)
      onEnableChange(checked)
    }
  }, [checked, checkedCached, onEnableChange])

  return (
    <>
      <Field
        className={clsx(
          'flex items-center relative cursor-pointer type-body-8',
          // Disabled state
          'has-[[data-disabled]]:text-neutral-600 has-[[data-disabled]]:cursor-not-allowed',
          className,
        )}
      >
        <Checkbox
          name={name}
          ref={ref}
          checked={enabled}
          onChange={onEnableChange}
          disabled={disabled}
          className={clsx(
            'checkbox peer group flex items-center',
            // Focus outline
            'after:absolute after:-top-2 after:-bottom-2 after:-left-2 after:-right-2 after:pointer-events-none after:rounded-8',
            'focus-visible:outline-none',
            'focus-visible:after:shadow-input-focus data-[focus]:after:shadow-input-focus-accent-link',
            'data-[disabled]:pointer-events-none',
          )}
        >
          <span
            className={clsx(
              'inline-flex items-center justify-center w-[17px] h-[17px] mr-2 relative rounded-[2px] shadow-input-active transition-[shadow] text-transparent bg-transparent',
              // Checked state
              'group-data-[checked]:text-black group-data-[checked]:bg-black',
              // Hover state
              'group-data-[hover]:shadow-input-hover',
              // Disabled state
              'group-data-[disabled]:shadow-checkbox-disabled group-data-[disabled]:text-neutral-600 group-data-[disabled]:bg-neutral-600',
              // Error state
              error ? 'shadow-[#D93900] group-data-[checked]:bg-[#D93900]' : '',
            )}
          >
            <Icon
              icon="check"
              className={clsx(
                'w-[17px] h-[17px] transition-colors',
                // Checked state
                'group-data-[checked]:!text-white',
                // Disabled state
                'group-data-[disabled]:text-neutral-600',
              )}
            />
          </span>
        </Checkbox>
        <Label
          className={clsx(
            'cursor-pointer peer-[[data-disabled]]:cursor-not-allowed',
            labelClassName,
          )}
        >
          {label}
        </Label>
      </Field>
      <InputError>{error}</InputError>
    </>
  )
})

// Controlled form element that is automatically registered to react-hook-form
export default function CheckboxInput({
  name,
  control,
  rules,
  checked = false,
  disabled = false,
  label = '',
  className = '',
  onChange,
  ...props
}: CheckboxInputProps) {
  return (
    <Controller
      name={name ?? ''}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <CheckboxInputStandalone
          {...{ checked, label, className, disabled }}
          {...field}
          {...props}
          error={fieldState.error?.message}
          onChange={(data) => {
            field.onChange?.(data)
            onChange?.(data)
          }}
        />
      )}
    />
  )
}
